import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Paper, Typography, TextField, Button,
  Box, Alert, Stack, MenuItem, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText, ClickAwayListener,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import Map, { Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useNavigate, useParams } from 'react-router-dom';
import { getTrip, addActivity, updateActivity } from '../services/api';
import config from '../config';

const CATEGORIES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'attraction', label: 'Attraction' },
  { value: 'activity', label: 'Activity' },
  { value: 'other', label: 'Other' },
];

function getSmartNameAndCategory(feature) {
  const cat = (feature.properties?.category || '').toLowerCase();
  const name = feature.text || '';
  const nameLower = name.toLowerCase();
  const hour = new Date().getHours();
  const meal = hour < 11 ? 'Breakfast' : hour < 15 ? 'Lunch' : hour < 21 ? 'Dinner' : 'Late Night Snack';

  if (cat.includes('airport') || nameLower.includes('airport')) {
    return { smartName: 'Flying', category: 'activity' };
  }
  if (cat.includes('restaurant') || cat.includes('food') || cat.includes('cafe') || cat.includes('bar') || cat.includes('bakery') || cat.includes('coffee')) {
    return { smartName: meal, category: 'restaurant' };
  }
  if (cat.includes('hotel') || cat.includes('lodging') || cat.includes('motel') || cat.includes('resort') || nameLower.includes('hotel') || nameLower.includes('resort')) {
    return { smartName: `Staying at ${name}`, category: 'hotel' };
  }
  if (cat.includes('museum') || cat.includes('gallery') || cat.includes('monument') || cat.includes('historic')) {
    return { smartName: `Visiting ${name}`, category: 'attraction' };
  }
  if (cat.includes('park') || cat.includes('beach') || cat.includes('trail') || cat.includes('recreation')) {
    return { smartName: name, category: 'activity' };
  }
  if (cat.includes('theme_park') || cat.includes('amusement')) {
    return { smartName: `Day at ${name}`, category: 'attraction' };
  }
  return { smartName: name, category: 'attraction' };
}

export default function AddEditActivity() {
  const { tripId, activityId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(activityId);

  const [form, setForm] = useState({
    name: '', category: 'activity', date: '', notes: '', websiteUrl: '',
  });
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      getTrip(tripId).then(({ data }) => {
        const act = data.activities?.find((a) => a.activityId === activityId);
        if (act) {
          setForm({
            name: act.name || '',
            category: act.category || 'activity',
            date: act.date || '',
            notes: act.notes || '',
            websiteUrl: act.websiteUrl || '',
          });
          if (act.location) {
            setSelectedLocation(act.location);
            setLocationQuery(act.location.name || act.location.address || '');
          }
        }
      });
    }
  }, [tripId, activityId, isEdit]);

  const sessionToken = useRef(crypto.randomUUID());

  const searchPlaces = useCallback(async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(query)}&access_token=${config.mapboxToken}&session_token=${sessionToken.current}&limit=6&types=poi,address,place,locality`
      );
      const data = await res.json();
      setSuggestions(data.suggestions || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchPlaces(locationQuery), 300);
    return () => clearTimeout(timer);
  }, [locationQuery, searchPlaces]);

  const handleSelectSuggestion = async (suggestion) => {
    setSuggestions([]);
    setShowSuggestions(false);
    setLocationQuery(suggestion.name + (suggestion.place_formatted ? `, ${suggestion.place_formatted}` : ''));
    setSearching(true);
    try {
      const res = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id}?access_token=${config.mapboxToken}&session_token=${sessionToken.current}`
      );
      const data = await res.json();
      const feature = data.features?.[0];
      if (feature) {
        const [lng, lat] = feature.geometry.coordinates;
        const props = feature.properties || {};
        const poiCategories = (props.poi_category || []).join(' ');
        const fakeFeature = { text: suggestion.name, properties: { category: poiCategories } };
        const { smartName, category } = getSmartNameAndCategory(fakeFeature);
        setSelectedLocation({
          name: suggestion.name,
          address: props.full_address || suggestion.place_formatted || suggestion.name,
          lat,
          lng,
          mapboxId: suggestion.mapbox_id,
        });
        setLocationQuery(props.full_address || suggestion.place_formatted || suggestion.name);
        const website = props.metadata?.website || props.website || '';
        setForm((f) => ({
          ...f,
          name: f.name || smartName,
          category,
          websiteUrl: f.websiteUrl || website,
        }));
        sessionToken.current = crypto.randomUUID();
      }
    } catch {
      setError('Could not load location details.');
    } finally {
      setSearching(false);
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Activity name is required.');
    setSaving(true);
    setError('');
    const payload = {
      name: form.name,
      category: form.category,
      date: form.date,
      notes: form.notes,
      websiteUrl: form.websiteUrl,
      location: selectedLocation || null,
    };
    try {
      if (isEdit) {
        await updateActivity(tripId, activityId, payload);
      } else {
        await addActivity(tripId, payload);
      }
      navigate(`/trips/${tripId}`);
    } catch {
      setError('Failed to save activity. Please try again.');
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 6 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/trips/${tripId}`)} sx={{ mb: 2 }}>
        Back to Trip
      </Button>
      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {isEdit ? 'Edit Activity' : 'Add Activity'}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>

            {/* Location Search */}
            <Box>
              <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    label="Search Location"
                    fullWidth
                    value={locationQuery}
                    onChange={(e) => { setLocationQuery(e.target.value); setSelectedLocation(null); }}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="e.g. Disney World, Times Square..."
                    InputProps={{
                      endAdornment: searching ? <CircularProgress size={18} /> : null,
                    }}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <Paper sx={{ position: 'absolute', zIndex: 1000, width: '100%', maxHeight: 280, overflow: 'auto', boxShadow: 4 }}>
                      <List dense disablePadding>
                        {suggestions.map((suggestion) => (
                          <ListItem
                            key={suggestion.mapbox_id}
                            button
                            onClick={() => handleSelectSuggestion(suggestion)}
                            sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer' }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <PlaceIcon color="primary" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={suggestion.name}
                              secondary={suggestion.place_formatted || suggestion.address}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>

              {/* Mini Map Preview */}
              {selectedLocation && (
                <Box sx={{ mt: 1.5, borderRadius: 2, overflow: 'hidden', height: 200 }}>
                  <Map
                    mapboxAccessToken={config.mapboxToken}
                    initialViewState={{ latitude: selectedLocation.lat, longitude: selectedLocation.lng, zoom: 14 }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle={config.mapStyle}
                    interactive={false}
                  >
                    <NavigationControl position="top-right" showCompass={false} />
                    <Marker latitude={selectedLocation.lat} longitude={selectedLocation.lng} anchor="bottom">
                      <PlaceIcon sx={{ color: '#e91e63', fontSize: 36, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }} />
                    </Marker>
                  </Map>
                </Box>
              )}
            </Box>

            <TextField label="Activity Name" required fullWidth value={form.name} onChange={set('name')} />
            <TextField select label="Category" fullWidth value={form.category} onChange={set('category')}>
              {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Date" type="date" fullWidth value={form.date} onChange={set('date')} InputLabelProps={{ shrink: true }} />
            <TextField
              label="Notes" fullWidth multiline rows={3}
              value={form.notes} onChange={set('notes')}
              placeholder="How was it? Any tips for others?"
            />
            <TextField
              label="Website URL" fullWidth
              value={form.websiteUrl} onChange={set('websiteUrl')}
              placeholder="https://..."
            />
            <Button type="submit" variant="contained" size="large" disabled={saving || !form.name.trim()}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Activity'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
